/**
 * **climax の演出が共有する道具**（Issue #544。索引 #539）。
 *
 * `effects/` には**イベントのファイルしか置かない**（設計文書 R2）ので、共有はここ。
 * **three は import しない** —— どれも `ctx.THREE` / `ctx.kit` を受け取って使う（守ること 8b）。
 * 時計も持たない（動きは `core/effect/motion.js` の時刻の純関数）。
 *
 * ## 座標
 *
 * - `space: 'screen'` … y 下向きの舞台のピクセル（1920×1080）。`kit` のテクスチャ（`flipY = false`）はそのまま正立
 * - `space: 'board'` … 透視のワールド。原点は平面の中心・盤は y = 0。`ctx.anchor(at).world` がそのまま使える
 */
import { CARD_ASPECT } from '../../../core/field/zones';
import { PLAYER_COLORS } from '../../../core/hud';
import { cardFaceImage, cardLabel } from '../../../core/board';

/** 書体（Issue #549。源真ゴシック Heavy。`canvas/canvas.css` の `@font-face`） */
export const FONT = '"GenShinGothic Heavy FX", "Hiragino Kaku Gothic StdN", "Yu Gothic UI", sans-serif';

/** プレイヤーの色（HUD の値から。無ければ既定） */
export const playerColor = (ctx, p) => ctx.hud?.players?.[p === 1 ? 1 : 0]?.color ?? PLAYER_COLORS[p === 1 ? 1 : 0];

/** カードの名前（無ければ空。**中身の分からないカードは空**＝ `core/board::cardLabel`。Issue #766） */
export const cardName = (ctx, code) => cardLabel(ctx.cards, code);

/**
 * カードの面の絵（表で中身が分かれば絵、裏か**中身の分からないカード**ならそのプレイヤーのスリーブ。Issue #766）。
 * `faceDown` はパネルの spec の語彙（`faceUp` の逆）。
 */
export const faceArt = (ctx, { code = null, player = 0, faceDown = false } = {}) => (
  cardFaceImage(ctx.cards, { code, player, faceUp: !faceDown })
);

/** 子を足して描く順を決める（透明なものは描く順が全て。depthTest を使わない） */
export function add(root, obj, order) {
  obj.renderOrder = order;
  obj.traverse?.((o) => { o.renderOrder = order; });
  root.add(obj);
  return obj;
}

/** テクスチャの板（`blend: 'add'` で光る） */
export function sprite(ctx, texture, w, h, { blend = 'normal', color = 0xffffff } = {}) {
  const mat = blend === 'add' ? ctx.kit.additive(texture, color, 1) : ctx.kit.plain(texture, 1);
  return ctx.kit.quad(mat, w, h);
}

/** 色で塗った板（暗転・閃光） */
export function fill(ctx, color, w, h, { blend = 'normal' } = {}) {
  const { THREE } = ctx;
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 1,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: blend === 'add' ? THREE.AdditiveBlending : THREE.NormalBlending,
  });
  return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
}

/** 濃さ（0 なら隠す。子を持つ group なら全部に掛ける） */
export function fade(obj, a) {
  const alpha = Math.max(0, Math.min(1, a));
  obj.visible = alpha > 0.001;
  const apply = (o) => {
    const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
    mats.forEach((m) => {
      if (m.userData.baseOpacity == null) m.userData.baseOpacity = m.opacity ?? 1;
      m.opacity = m.userData.baseOpacity * alpha;
    });
  };
  if (obj.traverse) obj.traverse(apply); else apply(obj);
  return obj;
}

/** 位置・倍率・回し（z 軸）を一度に */
export function place(obj, x, y, { s = 1, sx = s, sy = s, rot = 0, z = 0 } = {}) {
  obj.position.set(x, y, z);
  obj.scale.set(sx, sy, 1);
  obj.rotation.z = rot;
  return obj;
}

/**
 * **カードの板**（表なら絵、裏ならスリーブ）。絵が届いていなければ地の色（器が絵の到着で組み直す）。
 * `w` はカードの幅（高さは縦横比から）。
 */
export function card(ctx, { code = null, player = 0, faceUp = true, w = 300, brightness = 1.15 } = {}) {
  const img = cardFaceImage(ctx.cards, { code, player, faceUp });
  const tex = ctx.kit.imageTexture(img ?? null);
  const mat = ctx.kit.cardArtMaterial(tex, { brightness, saturation: 1.3 });
  if (!tex) mat.color?.set?.(faceUp ? '#28324e' : '#2a1a4d');
  const h = w / CARD_ASPECT;
  const mesh = ctx.kit.quad(mat, w, h);
  return { mesh, w, h };
}

/**
 * **カードのイラストだけ**の板（召喚バンクの主役。枠や文字を見せない）。絵が無ければ地の色の板。
 * `w` は幅（高さはイラストの縦横比 ≒ 正方形）。**中身の分からないカードは `player` のスリーブ**（Issue #766）。
 */
export function cardArt(ctx, { code = null, player = 0, w = 600, brightness = 1.15, saturation = 1.35 } = {}) {
  const img = cardFaceImage(ctx.cards, { code, player });
  const tex = ctx.kit.imageTexture(img ?? null);
  const mat = ctx.kit.cardArtMaterial(tex, { brightness, saturation });
  if (!tex) mat.color?.set?.('#1c2440');
  const mesh = ctx.kit.artQuad(mat, w);
  return { mesh, w, h: w / (ctx.kit.CARD_ART_ASPECT || 1), hasArt: Boolean(tex) };
}

/** 画面の大きさ（既定 1920×1080） */
export const canvasOf = (ctx) => ctx.stage?.canvas ?? { w: 1920, h: 1080 };

export { CARD_ASPECT };

/**
 * ゾーンが舞台のピクセルのどこに写っているか（外接する箱と中心）。写らなければ null。
 * 破片やカットインなど、**舞台のピクセルで**カードの大きさに合わせたいときに使う。
 */
export function zoneBox(ctx, zone) {
  const q = zone ? ctx.stage?.field?.zoneQuad?.(zone) : null;
  if (!q) return null;
  const xs = q.map((p) => p[0]); const ys = q.map((p) => p[1]);
  const x = Math.min(...xs); const y = Math.min(...ys);
  const w = Math.max(...xs) - x; const h = Math.max(...ys) - y;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
}

/** HUD の LP の箱の中心（ダメージの数字が飛ぶ先）。無ければ上の左右 */
export function lifeCenter(ctx, p) {
  const b = ctx.stage?.hud?.life?.(p === 1 ? 1 : 0);
  const { w: W } = canvasOf(ctx);
  if (!b) return [p === 1 ? W * 0.8 : W * 0.2, 90];
  return [b.x + b.w / 2, b.y + b.h / 2];
}

/**
 * **ステータスの数字の色**（Issue #584。ユーザー 2026-09-28: 元々の値より上がっていれば赤、下がっていれば青）。
 * 盤のカードの ATK / DEF の帯・召喚成功のパネル・対戦のパネル（大きい / 盤に残る小さい）が同じ値を使う。
 * テーマの語彙に合わせた硬い色（光らせない）。**原因は読まず、今の値と元々の値の比較だけ**で決める。
 */
export const STAT_UP = '#ff3b30';
export const STAT_DOWN = '#3d8bff';

/** 今の値と元々の値の比較（`'up'` / `'down'` / 同じ・分からなければ null） */
export function statTone(value, base) {
  if (!Number.isFinite(value) || !Number.isFinite(base) || value === base) return null;
  return value > base ? 'up' : 'down';
}

/** 数字の色（上がっていれば `STAT_UP`、下がっていれば `STAT_DOWN`、同じ・分からなければ `fallback`＝今の色） */
export function statColor(value, base, fallback) {
  const tone = statTone(value, base);
  if (tone === 'up') return STAT_UP;
  if (tone === 'down') return STAT_DOWN;
  return fallback;
}

/** 盤面の `stats`（`{ atk, def, level, base_atk, base_def }`）から元々の値を引く（無ければ null） */
export function statBase(stats) {
  const n = (v) => (Number.isFinite(v) ? v : null);
  return { atk: n(stats?.base_atk ?? stats?.baseAtk), def: n(stats?.base_def ?? stats?.baseDef) };
}
